import * as React from 'react'
import { ChangeEventHandler, useMemo } from 'react'
import classnames from 'classnames'
import { AccordionUncontrolled as Accordion } from '../../molecules/Accordion/Accordion'
import { Checkbox, CheckboxGroupProps } from '../Form'
import { useCallback } from 'react'
import { useUniqueOptions } from '../../../hooks/useUniqueOptions/useUniqueOptions'
import { Option } from '../Form/types'

export type NestedCheckboxGroupOptionType = {
    value: string
    label: React.ReactNode
    indeterminate?: boolean
    children?: Array<NestedCheckboxGroupOptionType>
}

type NestedCheckboxGroupProps = React.PropsWithChildren & {
    options: Array<NestedCheckboxGroupOptionType>
    checkedItems: string[]
    onChange: (newCheckedItems: string[]) => void
    checkboxLabelWrapperClassname?: string
    itemWrapperClassname?: string
    wrapperClassname?: string
    renderLabel?: CheckboxGroupProps['renderLabel']
    toggleWrapperClassname?: string
}

export const NestedCheckboxGroup: React.FC<NestedCheckboxGroupProps> = ({
    options,
    checkedItems,
    onChange,
    checkboxLabelWrapperClassname,
    itemWrapperClassname,
    wrapperClassname,
    renderLabel,
    toggleWrapperClassname,
}) => {
    // Deduplicate options by value, keeping the first occurrence
    const uniqueOptions = useUniqueOptions(options)

    const handleChange = (value: string, isChecked: boolean) => {
        // Add or remove the value from the checkedItems array
        const newCheckedItems = checkedItems.slice()

        if (isChecked) {
            newCheckedItems.push(value)
            onChange(newCheckedItems)
            return
        }

        const index = newCheckedItems.indexOf(value)

        if (index > -1) {
            newCheckedItems.splice(index, 1)
            onChange(newCheckedItems)
            return
        }

        onChange(newCheckedItems)
    }

    return (
        <div className={wrapperClassname}>
            {uniqueOptions?.map((option) => (
                <NestedCheckboxGroupOption
                    key={option.value}
                    {...option}
                    label={
                        renderLabel
                            ? renderLabel(option as Option<string>)
                            : option.label
                    }
                    checkedItems={checkedItems}
                    onChange={handleChange}
                    checkboxLabelWrapperClassname={classnames(
                        'text-ellipsis overflow-hidden',
                        checkboxLabelWrapperClassname
                    )}
                    itemWrapperClassname={itemWrapperClassname}
                    toggleWrapperClassname={toggleWrapperClassname}
                />
            ))}
        </div>
    )
}

type NestedCheckboxGroupOptionProps = NestedCheckboxGroupOptionType & {
    checkedItems: string[] // Array of selected values
    onChange: (value: string, isChecked: boolean) => void
    checkboxLabelWrapperClassname?: string
    itemWrapperClassname?: string
    toggleWrapperClassname?: string
}

export const NestedCheckboxGroupOption: React.FC<
    NestedCheckboxGroupOptionProps
> = (props) => {
    const {
        value,
        label,
        children,
        checkedItems,
        onChange,
        checkboxLabelWrapperClassname,
        indeterminate,
        toggleWrapperClassname,
    } = props
    const isChecked = checkedItems.includes(value)

    const handleCheckboxChange: ChangeEventHandler<HTMLInputElement> = (
        event
    ) => {
        onChange(value, event.target.checked)
    }

    const itemWrapperClassname = classnames('grow', props.itemWrapperClassname)

    const isOpen = useCallback(
        (option: NestedCheckboxGroupOptionType) => {
            return (
                option.children?.some((option) =>
                    checkedItems.includes(option.value)
                ) || false
            )
        },
        [checkedItems]
    )

    const openItems = useMemo(() => {
        return isOpen({
            value: props.value,
            label: props.label,
            children: props.children,
        })
            ? [props.value]
            : []
    }, [isOpen, props.children, props.label, props.value])

    if (!children || children.length === 0) {
        return (
            <Checkbox
                indeterminate={indeterminate}
                name={`checkbox-${value}`}
                label={label}
                isBoxed
                checked={isChecked}
                onChange={handleCheckboxChange}
                itemWrapperClassname={itemWrapperClassname}
                labelWrapperClassname={checkboxLabelWrapperClassname}
            />
        )
    }

    return (
        <Accordion
            name={value}
            openItems={openItems}
            toggleWrapperClassname={toggleWrapperClassname}
        >
            <Accordion.ItemToggle name={value}>
                <Checkbox
                    indeterminate={indeterminate}
                    name={`checkbox-${value}`}
                    label={label}
                    isBoxed
                    checked={isChecked}
                    onChange={handleCheckboxChange}
                    itemWrapperClassname={itemWrapperClassname}
                    labelWrapperClassname={checkboxLabelWrapperClassname}
                />
            </Accordion.ItemToggle>
            <Accordion.ItemContent name={value}>
                <div className="pl-4">
                    {children.map((child) => (
                        <NestedCheckboxGroupOption
                            key={child.value}
                            {...child}
                            checkedItems={checkedItems}
                            onChange={onChange}
                            itemWrapperClassname={itemWrapperClassname}
                            checkboxLabelWrapperClassname={
                                checkboxLabelWrapperClassname
                            }
                            toggleWrapperClassname={toggleWrapperClassname}
                        />
                    ))}
                </div>
            </Accordion.ItemContent>
        </Accordion>
    )
}
